import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the session from the URL hash
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Error getting session:', error);
          navigate('/login');
          return;
        }

        if (data.session) {
          // Call backend /auth/me to get/create user with proper approval status
          // Backend will respect AUTO_APPROVE_USERS toggle
          const token = data.session.access_token;

          try {
            const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/auth/me`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (!response.ok) {
              throw new Error('Failed to get user data');
            }

            const userData = await response.json();

            // Check user's approval status from backend
            const { data: dbUser } = await supabase
              .from('users')
              .select('approval_status, onboarding_done')
              .eq('id', userData.id)
              .single();

            const approvalStatus = dbUser?.approval_status || 'approved';

            if (approvalStatus === 'pending') {
              // Clear session and wait for auth state to propagate
              await supabase.auth.signOut();

              // Small delay to ensure session is fully cleared before navigation
              await new Promise(resolve => setTimeout(resolve, 100));

              // Use replace to prevent going back to callback
              navigate('/waitlist-confirmation', { replace: true });
              return;
            }

            if (approvalStatus === 'rejected') {
              await supabase.auth.signOut();
              setError('Your account registration was rejected');
              setTimeout(() => navigate('/login', { replace: true }), 2000);
              return;
            }

            // Approved - check for OAuth consent redirect first
            // First check sessionStorage (for Google OAuth - Supabase strips query params during OAuth round-trip)
            // Then fall back to URL params (for email/password login redirect)
            const storedRedirect = sessionStorage.getItem('oauth_redirect_after_login');
            const urlParams = new URLSearchParams(window.location.search);
            const oauthRedirect = storedRedirect || urlParams.get('redirect');

            console.log('[AuthCallback] Full URL:', window.location.href);
            console.log('[AuthCallback] Search params:', window.location.search);
            console.log('[AuthCallback] Stored redirect (sessionStorage):', storedRedirect);
            console.log('[AuthCallback] URL redirect param:', urlParams.get('redirect'));
            console.log('[AuthCallback] Final OAuth redirect:', oauthRedirect);

            // Clear sessionStorage to prevent stale redirects on future logins
            if (storedRedirect) {
              sessionStorage.removeItem('oauth_redirect_after_login');
            }

            if (oauthRedirect && oauthRedirect.startsWith('/')) {
              // Decode the redirect URL in case it was double-encoded
              const decodedRedirect = decodeURIComponent(oauthRedirect);
              console.log('[AuthCallback] Decoded redirect:', decodedRedirect);
              // Redirect to OAuth consent page
              navigate(decodedRedirect, { replace: true });
              return;
            }

            // Check if onboarding is done
            const onboardingDone = dbUser?.onboarding_done || false;

            if (onboardingDone) {
              // User already completed onboarding, go to home
              navigate('/', { replace: true });
            } else {
              // New approved user, redirect to onboarding
              navigate('/onboarding/step-1', { replace: true });
            }
          } catch (fetchError) {
            console.error('Error fetching user data:', fetchError);
            // If fetch fails, try direct login redirect
            navigate('/login', { replace: true });
          }
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Error in auth callback:', error);
        setError(error instanceof Error ? error.message : 'Authentication failed');
        setTimeout(() => navigate('/login'), 2000);
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-400 to-teal-500">
      <div className="text-center">
        {error ? (
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <p className="text-red-600 text-lg mb-2">Authentication Error</p>
            <p className="text-gray-700">{error}</p>
            <p className="text-sm text-gray-500 mt-2">Redirecting to login...</p>
          </div>
        ) : (
          <div className="text-white text-xl">Processing authentication...</div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
